// src/components/CarFilterBar.tsx

import {
  Box,
  Button,
  TextField,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Stack,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Tooltip,
  Divider,
  Radio,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ClearIcon from '@mui/icons-material/Clear';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Custom Components
import RightDrawer from './RightDrawer';
import axiosInstance from '../services/axiosInstance';
import { encodeId, decodeId } from '../utils/idEncoder';

// --- TYPES ---
type CarFilterBarProps = {
  viewMode?: 'grid' | 'list';
  onToggleViewMode?: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  url?: string;
  onFilterChange?: (key: string, value: string) => void;
};

const ENCRYPTED_KEYS = [
  'vehicleBrandIds',
  'vehicleBodyTypeIds',
  'vehicleFuelTypeIds',
  'vehicleTransmissionTypeIds',
  'vehicleClassIds',
  'dealerIds'
];

interface ApiDataResponse {
  responseCode: string;
  responseData?: {
    dealerCompanyData?: any[];
    vehicleBodyTypeData?: any[];
    vehicleFuelTypeData?: any[];
    vehicleTransmissionTypeData?: any[];
    vehicleBrandData?: any[];
  }
}

// --- DYNAMIC RANGE COMPONENT ---
const RangeDropdownContent = ({
  title,
  minParamKey,
  maxParamKey,
  options,
  formatLabel,
  onClose
}: {
  title: string;
  minParamKey: string;
  maxParamKey: string;
  options: number[];
  formatLabel: (val: number) => string;
  onClose: () => void;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const currentMin = searchParams.get(minParamKey) ? Number(searchParams.get(minParamKey)) : '';
  const currentMax = searchParams.get(maxParamKey) ? Number(searchParams.get(maxParamKey)) : '';

  const [minVal, setMinVal] = useState<number | ''>(currentMin);
  const [maxVal, setMaxVal] = useState<number | ''>(currentMax);

  useEffect(() => {
    setMinVal(currentMin);
    setMaxVal(currentMax);
  }, [currentMin, currentMax]);

  const handleApply = () => {
    const params = new URLSearchParams(location.search);
    if (minVal !== '') params.set(minParamKey, minVal.toString());
    else params.delete(minParamKey);

    if (maxVal !== '') params.set(maxParamKey, maxVal.toString());
    else params.delete(maxParamKey);

    params.delete('page');
    navigate(`${location.pathname}?${params.toString()}`);
    onClose();
  };

  const handleClear = () => {
    setMinVal('');
    setMaxVal('');
    const params = new URLSearchParams(location.search);
    params.delete(minParamKey);
    params.delete(maxParamKey);
    params.delete('page');
    navigate(`${location.pathname}?${params.toString()}`);
    onClose();
  };

  return (
    <Box p={3} width={340} onClick={(e) => e.stopPropagation()}>
      <Typography variant="subtitle2" gutterBottom fontWeight="bold">{title}</Typography>
      <Stack direction="row" spacing={2} alignItems="center" my={2}>
        <TextField
          select
          fullWidth
          size="small"
          label="Lowest"
          value={minVal}
          onChange={(e) => setMinVal(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <MenuItem value=""><em>Any</em></MenuItem>
          {options.map(opt => (
            <MenuItem key={`min-${opt}`} value={opt} disabled={maxVal !== '' && opt > maxVal}>
              {formatLabel(opt)}
            </MenuItem>
          ))}
        </TextField>
        
        <Typography variant="body2" color="text.secondary" fontWeight={500}>to</Typography>
        
        <TextField
          select
          fullWidth
          size="small"
          label="Highest"
          value={maxVal}
          onChange={(e) => setMaxVal(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <MenuItem value=""><em>Any</em></MenuItem>
          {options.map(opt => (
            <MenuItem key={`max-${opt}`} value={opt} disabled={minVal !== '' && opt < minVal}>
              {formatLabel(opt)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction="row" spacing={1} justifyContent="space-between" mt={1}>
        <Button size="small" color="inherit" onClick={handleClear}>Clear</Button>
        <Button size="small" variant="contained" onClick={handleApply} sx={{ px: 3 }}>Apply</Button>
      </Stack>
    </Box>
  );
};


const CarFilterBar = ({
  viewMode,
  onToggleViewMode,
  searchQuery,
  onSearchChange,
  url,
  onFilterChange,
}: CarFilterBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // --- STATE ---
  const [dealerData, setDealerData] = useState<any[]>([]);
  const [fuelTypeData, setvehicleFuelTypeData] = useState<any[]>([]);
  const [TransmissionTypeData, setvehicleTransmissionTypeData] = useState<any[]>([]);
  const [bodyTypesData, setvehicleBodyTypeData] = useState<any[]>([]);
  const [brandData, setvehicleBrandData] = useState<any[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  const [tempSelected, setTempSelected] = useState<{ [key: string]: string[] }>({});
  const [initializedKeys, setInitializedKeys] = useState<Set<string>>(new Set());

  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [sortList, setSortList] = useState<{ key: string; direction: 'asc' | 'desc' }[]>([]);

  const searchParams = new URLSearchParams(location.search);
  const currentStatus = searchParams.get('statusId') || '';

  // --- MENU CONFIGURATIONS ---
  const statusOptions = [
    { label: 'Any Status', value: '' },
    { label: 'Available', value: 'VPB' },
    { label: 'Sold', value: 'VSL' },
  ];

  const sortOptions = [
    { label: 'Date Posted (Newest)', key: 'addDate', dir: 'desc' },
    { label: 'Date Posted (Oldest)', key: 'addDate', dir: 'asc' },
    { label: 'Price (Highest)', key: 'askingPrice', dir: 'desc' },
    { label: 'Price (Lowest)', key: 'askingPrice', dir: 'asc' },
    { label: 'Mileage (Highest)', key: 'mileage', dir: 'desc' },
    { label: 'Mileage (Lowest)', key: 'mileage', dir: 'asc' },
  ];

  // Derive dynamic labels for the button
  const activeStatusLabel = statusOptions.find(o => o.value === currentStatus)?.label || 'Any Status';
  const activeSortLabel = sortList.length > 0 
    ? sortOptions.find(o => o.key === sortList[0].key && o.dir === sortList[0].direction)?.label || 'Relevant'
    : 'Relevant';

  // --- RANGE OPTIONS GENERATOR ---
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 25 }, (_, i) => currentYear - i);
  const priceOptions = Array.from({ length: 60 }, (_, i) => (i + 1) * 5000);
  const depreciationOptions = Array.from({ length: 50 }, (_, i) => (i + 1) * 1000);
  const mileageOptions = Array.from({ length: 30 }, (_, i) => (i + 1) * 10000);

  const noScrollbarStyle = {
    overflowX: 'auto',
    display: 'flex',
    gap: 1.5,
    width: '100%',
    paddingBottom: '4px',
    '&::-webkit-scrollbar': { display: 'none' },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
  };

  // --- SEARCH SUBMIT LOGIC ---
  const handleSearchSubmit = () => {
    const params = new URLSearchParams(location.search);
    if (searchQuery.trim()) {
      params.set('searchKeyword', searchQuery.trim());
    } else {
      params.delete('searchKeyword');
    }
    params.delete('page');
    navigate(`${location.pathname}?${params.toString()}`);
  };

  // --- COMBINED SORT & STATUS LOGIC ---
  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => setSortAnchorEl(event.currentTarget);
  const handleSortClose = () => setSortAnchorEl(null);

  const handleExplicitSortChange = (key: string, direction: 'asc' | 'desc') => {
    const params = new URLSearchParams(location.search);
    params.set('sortKeys', key);
    params.set('sortDirs', direction);
    params.delete('page');
    navigate(`${url ?? '/used-vehicles'}?${params.toString()}`);
    handleSortClose();
  };

  const handleStatusChange = (newStatus: string) => {
    if (onFilterChange) {
      onFilterChange('statusId', newStatus);
    }
    handleSortClose();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const keys = params.get('sortKeys')?.split(',') || [];
    const dirs = params.get('sortDirs')?.split(',') || [];
    if (keys.length > 0) {
      setSortList(keys.map((key, i) => ({ key, direction: (dirs[i] === 'desc' ? 'desc' : 'asc') })));
    } else {
      setSortList([]); // reset if clear
    }
  }, [location.search]);

  // --- API DATA ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const payload = { dealerCompanyData: true, vehicleBodyTypeData: true, vehicleFuelTypeData: true, vehicleTransmissionTypeData: true, vehicleBrandData: true };
        const res = await axiosInstance.post("/CustomerVehicle/VehicleMultipleDataList", payload);
        const data = res.data as ApiDataResponse;
        if (data.responseCode === "00" && data.responseData) {
          setvehicleBodyTypeData(data.responseData.vehicleBodyTypeData || []);
          setvehicleFuelTypeData(data.responseData.vehicleFuelTypeData || []);
          setvehicleTransmissionTypeData(data.responseData.vehicleTransmissionTypeData || []);
          setvehicleBrandData(data.responseData.vehicleBrandData || []);
          setDealerData(data.responseData.dealerCompanyData || []);
        }
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, []);

  // --- UI HELPERS ---
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>, filter: string) => {
    event.stopPropagation();
    setSelectedLabel(filter);
    setAnchorEl(event.currentTarget);
    setActiveFilter(filter);
  };
  const handleClose = () => { setAnchorEl(null); setActiveFilter(null); };

  // --- CHECKBOX LOGIC ---
  const getTempSelected = (key: string): string[] => {
    if (!initializedKeys.has(key)) {
      const params = new URLSearchParams(location.search);
      const val = params.get(key);
      const parts = val ? val.split(',') : [];
      let decodedIds = parts;
      if (ENCRYPTED_KEYS.includes(key)) {
        decodedIds = parts.map(hash => decodeId(hash)?.toString() || '').filter(Boolean);
      }
      setTempSelected(prev => ({ ...prev, [key]: decodedIds }));
      setInitializedKeys(prev => new Set(prev).add(key));
      return decodedIds;
    }
    return tempSelected[key] || [];
  };

  const toggleSelection = (key: string, id: string) => {
    setTempSelected(prev => {
      const set = new Set(prev[key] || []);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...prev, [key]: Array.from(set) };
    });
  };

  const applySelection = (key: string) => {
    const rawIds = tempSelected[key] || [];
    let outputString = ENCRYPTED_KEYS.includes(key) ? rawIds.map(id => encodeId(id)).join(',') : rawIds.join(',');
    if (onFilterChange) onFilterChange(key, outputString);
    handleClose();
  };

  const clearSelection = (key: string) => {
    setTempSelected(prev => ({ ...prev, [key]: [] }));
    if (onFilterChange) onFilterChange(key, '');
    handleClose();
  };

  const renderCheckboxFilterMenu = (key: string, data: any[], idField: string, labelField: string) => (
    <Box sx={{ width: 240, maxHeight: 320, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, p: 1, overflowY: 'auto' }}>
        {data.map((item) => {
          const id = item[idField].toString();
          const checked = getTempSelected(key).includes(id);
          return (
            <MenuItem key={id} onClick={() => toggleSelection(key, id)} sx={{ borderRadius: 1, mb: 0.5 }}>
              <input type="checkbox" checked={checked} readOnly style={{ marginRight: 12, transform: 'scale(1.1)' }} />
              <Typography variant="body2">{item[labelField]}</Typography>
            </MenuItem>
          );
        })}
      </Box>
      <Box sx={{ borderTop: '1px solid #eee', p: 1.5, position: 'sticky', bottom: 0, bgcolor: '#fff' }}>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button size="small" color="inherit" onClick={() => clearSelection(key)}>Clear</Button>
          <Button size="small" variant="contained" onClick={() => applySelection(key)} sx={{ px: 3 }}>Apply</Button>
        </Stack>
      </Box>
    </Box>
  );

  const filterButtons = ['Brand & Model', 'Body Type', 'Fuel Type', 'Transmission', 'Car Price & Instalment', 'Year', 'Mileage', 'Depreciation'];
  const filterMap: any = {
    'Body Type': { key: 'vehicleBodyTypeIds', data: bodyTypesData, idField: 'vehicleBodyTypeId' },
    'Brand & Model': { key: 'vehicleBrandIds', data: brandData, idField: 'vehicleBrandId' },
    'Fuel Type': { key: 'vehicleFuelTypeIds', data: fuelTypeData, idField: 'vehicleFuelTypeId' },
    'Transmission': { key: 'vehicleTransmissionTypeIds', data: TransmissionTypeData, idField: 'vehicleTransmissionTypeId' },
    'Car Price & Instalment': { key: 'minAskingPrice', getLabel: (p: URLSearchParams) => p.get('minAskingPrice') ? `$${Number(p.get('minAskingPrice')).toLocaleString()}+` : null },
    'Year': { key: 'minManufacturingYear', getLabel: (p: URLSearchParams) => p.get('minManufacturingYear') ? `${p.get('minManufacturingYear')}+` : null },
    'Mileage': { key: 'minMileage', getLabel: (p: URLSearchParams) => p.get('minMileage') ? `${Number(p.get('minMileage')).toLocaleString()}km+` : null },
    'Depreciation': { key: 'minDepreciation', getLabel: (p: URLSearchParams) => p.get('minDepreciation') ? `$${Number(p.get('minDepreciation')).toLocaleString()}+` : null },
  };

  // --- RENDER ---
  return (
    <Box sx={{ width: '100%' }}>
      
      {/* --- ROW 1: Search & Utilities --- */}
      <Stack 
        direction={{ xs: 'column', lg: 'row' }} 
        spacing={2} 
        alignItems={{ xs: 'stretch', lg: 'center' }} 
        mb={2.5}
      >
        {/* Search Bar */}
        <Box sx={{ flexGrow: 1, maxWidth: { lg: 400 } }}>
          <TextField
            fullWidth
            placeholder="Search Make or Model..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => { 
              if (e.key === 'Enter') {
                handleSearchSubmit();
              }
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end" sx={{ height: '100%', maxHeight: '100%', ml: 0 }}>
                  {searchQuery && (
                    <IconButton 
                      size="small" 
                      onClick={() => { 
                        onSearchChange(''); 
                        const params = new URLSearchParams(location.search);
                        params.delete('searchKeyword');
                        params.delete('page');
                        navigate(`${location.pathname}?${params.toString()}`); 
                      }}
                      sx={{ mr: 1 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    onClick={handleSearchSubmit}
                    sx={{
                      height: '100%',
                      minWidth: '50px',
                      borderRadius: '0 8px 8px 0', // Sharp left corners, rounded right corners
                      px: 0,
                    }}
                  >
                    <SearchIcon />
                  </Button>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': { 
                borderRadius: '8px',
                bgcolor: 'background.paper',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                pr: 0, // CRITICAL: Removes the right padding so the button is flush
                overflow: 'hidden' // Keeps the button neatly clipped to the border
              }
            }}
          />
        </Box>

        <Box sx={{ flexGrow: 1, display: { xs: 'none', lg: 'block' } }} />

        {/* Utilities: Combined Sort/Status, Filter, View, Clear */}
        <Stack 
          direction="row" 
          spacing={1} 
          alignItems="center" 
          sx={{ 
            justifyContent: { xs: 'space-between', lg: 'flex-end' },
            width: { xs: '100%', lg: 'auto' },
            minWidth: 0 // Crucial to allow truncation in flexbox
          }}
        >
          
          {/* COMBINED SORT & STATUS BUTTON */}
          <Button 
            variant="text" 
            color="inherit" 
            onClick={handleSortClick}
            endIcon={<KeyboardArrowDownIcon sx={{ flexShrink: 0 }} />}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 500, 
              color: 'text.secondary',
              fontSize: '0.9rem',
              px: { xs: 0, sm: 1 },
              minWidth: 0, 
              flexShrink: 1,
              justifyContent: 'flex-start',
              '&:hover': { bgcolor: 'transparent', color: 'text.primary' }
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 'inherit', 
                textAlign: 'left',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {/* Hide the word 'Sort by' on tiny mobile screens to save space */}
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }}}>Sort by </Box>
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                {activeStatusLabel}, {activeSortLabel}
              </Box>
            </Typography>
          </Button>

          {/* RIGHT SIDE ICONS (Grouped so they never squish) */}
          <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center" sx={{ flexShrink: 0 }}>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: 'center', display: { xs: 'none', sm: 'block'} }} />

            <Tooltip title="All Filters">
              <Button 
                variant="outlined" 
                color="inherit" 
                size="small" 
                startIcon={<TuneIcon />} 
                onClick={() => setDrawerOpen(true)}
                sx={{ borderRadius: 6, borderColor: '#ddd', textTransform: 'none', fontWeight: 600 }}
              >
                Filters
              </Button>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: 'center' }} />

            {onToggleViewMode && (
              <Tooltip title={`Switch to ${viewMode === 'grid' ? 'List' : 'Grid'} View`}>
                <IconButton onClick={onToggleViewMode} sx={{ border: '1px solid #eee' }} size="small">
                  {viewMode === 'grid' ? <ViewListIcon fontSize="small" /> : <ViewModuleIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Clear All Filters">
              <IconButton color="error" size="small" onClick={() => { setSortList([]); navigate(url || '/used-vehicles'); }}>
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>

      {/* --- ROW 2: Quick Filter Pills (Horizontal Scroll) --- */}
      <Box sx={noScrollbarStyle}>
        {filterButtons.map(label => {
          const conf = filterMap[label];
          let subLabel = null;
          let isActive = false;

          if (conf) {
            const params = new URLSearchParams(location.search);
            if (conf.getLabel) subLabel = conf.getLabel(params);
            else {
              const count = (params.get(conf.key)?.split(',') || []).filter(Boolean).length;
              if (count > 0) subLabel = `${count}`;
            }
            if (subLabel) isActive = true;
          }

          const isMenuOpen = selectedLabel === label && Boolean(anchorEl);

          return (
            <Button
              key={label}
              variant={isActive ? "contained" : "outlined"}
              size="small"
              onClick={(e) => handleClick(e, label)}
              endIcon={<KeyboardArrowDownIcon sx={{ ml: -0.5 }} />}
              sx={{
                borderRadius: '20px', // Pill shape
                whiteSpace: 'nowrap',
                flexShrink: 0,
                px: 2,
                py: 0.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                color: isActive ? 'primary.contrastText' : 'text.primary',
                bgcolor: isActive ? 'primary.main' : 'background.paper',
                borderColor: isActive ? 'primary.main' : '#ddd',
                boxShadow: isMenuOpen ? '0 0 0 2px rgba(0,0,0,0.1)' : 'none',
                '&:hover': { 
                  bgcolor: isActive ? 'primary.dark' : '#f5f5f5',
                  borderColor: isActive ? 'primary.dark' : '#ccc',
                }
              }}
            >
              {label}
              {subLabel && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    bgcolor: isActive ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)',
                    px: 0.8,
                    py: 0.2,
                    borderRadius: 2,
                    fontSize: '0.7rem',
                  }}
                >
                  {subLabel}
                </Box>
              )}
            </Button>
          );
        })}
      </Box>

      {/* --- COMBINED SORT & STATUS MENU --- */}
      <Menu 
        anchorEl={sortAnchorEl} 
        open={Boolean(sortAnchorEl)} 
        onClose={handleSortClose}
        PaperProps={{ elevation: 4, sx: { minWidth: 240, borderRadius: 3, mt: 1, py: 1 } }}
      >
        <Typography variant="caption" sx={{ px: 2, pb: 0.5, color: 'text.disabled', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Status
        </Typography>
        {statusOptions.map(opt => (
          <MenuItem 
            key={opt.label} 
            onClick={() => handleStatusChange(opt.value)}
            sx={{ py: 0.5, px: 1.5 }}
          >
            <Radio 
              checked={currentStatus === opt.value} 
              size="small" 
              color="primary"
              sx={{ p: 0.5, mr: 1 }} 
            />
            <Typography variant="body2" fontWeight={currentStatus === opt.value ? 600 : 400}>
              {opt.label}
            </Typography>
          </MenuItem>
        ))}

        <Divider sx={{ my: 1 }} />

        <Typography variant="caption" sx={{ px: 2, pb: 0.5, color: 'text.disabled', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Sort By
        </Typography>
        {sortOptions.map(opt => {
          const isActive = sortList.length > 0 && sortList[0].key === opt.key && sortList[0].direction === opt.dir;
          return (
            <MenuItem 
              key={opt.label} 
              onClick={() => handleExplicitSortChange(opt.key, opt.dir as 'asc' | 'desc')}
              sx={{ py: 0.5, px: 1.5 }}
            >
              <Radio 
                checked={isActive} 
                size="small" 
                color="primary"
                sx={{ p: 0.5, mr: 1 }} 
              />
              <Typography variant="body2" fontWeight={isActive ? 600 : 400}>
                {opt.label}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* --- QUICK FILTER DROPDOWN MENUS --- */}
      <Menu 
        anchorEl={anchorEl} 
        open={Boolean(anchorEl)} 
        onClose={handleClose} 
        PaperProps={{ elevation: 4, sx: { borderRadius: 3, mt: 1 } }}
      >
        {activeFilter === 'Brand & Model' && renderCheckboxFilterMenu('vehicleBrandIds', brandData, 'vehicleBrandId', 'description')}
        {activeFilter === 'Body Type' && renderCheckboxFilterMenu('vehicleBodyTypeIds', bodyTypesData, 'vehicleBodyTypeId', 'description')}
        {activeFilter === 'Fuel Type' && renderCheckboxFilterMenu('vehicleFuelTypeIds', fuelTypeData, 'vehicleFuelTypeId', 'description')}
        {activeFilter === 'Transmission' && renderCheckboxFilterMenu('vehicleTransmissionTypeIds', TransmissionTypeData, 'vehicleTransmissionTypeId', 'description')}
        
        {/* DROPDOWN RANGES - Automatically applies logic perfectly for each type */}
        {activeFilter === 'Year' && (
          <RangeDropdownContent title="Manufacturing Year" minParamKey="minManufacturingYear" maxParamKey="maxManufacturingYear" options={yearOptions} formatLabel={(v) => v.toString()} onClose={handleClose} />
        )}
        {activeFilter === 'Car Price & Instalment' && (
          <RangeDropdownContent title="Price Range" minParamKey="minAskingPrice" maxParamKey="maxAskingPrice" options={priceOptions} formatLabel={(v) => `$${v.toLocaleString()}`} onClose={handleClose} />
        )}
        {activeFilter === 'Depreciation' && (
          <RangeDropdownContent title="Depreciation Range" minParamKey="minDepreciation" maxParamKey="maxDepreciation" options={depreciationOptions} formatLabel={(v) => `$${v.toLocaleString()}`} onClose={handleClose} />
        )}
        {activeFilter === 'Mileage' && (
          <RangeDropdownContent title="Mileage Range" minParamKey="minMileage" maxParamKey="maxMileage" options={mileageOptions} formatLabel={(v) => `${v.toLocaleString()} km`} onClose={handleClose} />
        )}
      </Menu>

      <RightDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} fuelTypeData={fuelTypeData} TransmissionTypeData={TransmissionTypeData} bodyTypesData={bodyTypesData} brandData={brandData} dealerData={dealerData} />
    </Box>
  );
};

export default CarFilterBar;